import depositApi from "../api/deposit.api";
import {
  abcAppId,
  abcCryptoAppId,
  abcUtrAppId,
  abcUtrTwoAppId,
  abcUtrThreeAppId,
  abcUtrFourAppId,
  abcNonUtrTwoAppId,
  abcUtrFiveAppid,
  abcCryptoUrl,
  abcPgUrl,
} from "../../../config/appConfig";
import {
  AvailableDepositBonusesResponse,
  UserDepositSummaryResponse,
  UserPendingDepositsResponseList,
  DepositTypeListResponseList,
  DepositConfigurationResponse,
  ValidateBonusEligibilityResponse,
  AvailableDepositBonusesList,
  UserMobileNumberDetailResponse,
  BankTransferMethod,
  DepositCreateResponse,
  TopRecommendedTransferMethodsResponse,
  UserRecentUsedDepositTypesResponse,
  DepositPaymentMethodListResponseList,
} from "../models/response";
import {
  AvailableDepositBonusesRequest,
  ValidateBonusEligibilityRequest,
  AbcPaymentDepositInitiateReuest,
  DepositCreateRequest,
  AbcDepositCreateRequest,
  DragonDepositCreateRequest,
  GatewayHubDepositCreateRequest,
  DepositTypeListRequest,
  PayzEasyDepositInitiateRequest,
} from "../models/request";
import {
  DepositStatus,
  DepositMethodType,
  DepositPaymentMethod,
  DepositFlow,
  DepositType,
} from "../models/constants";
import { ABCDOMContentLoaded, ABCCryptoDOMContentLoaded } from "../../../assets/scripts/abc";
import localization from "../../../config/localization";
import { loadScript } from "../../../assets/scripts/loadscript";
import errorModal from "../../../services/errorModal.service";
import { AccessMode } from "../../../models/constants/accessMode";

const depositv1Service = {
  getUserPendingDeposits: async (): Promise<{
    pendingOnlineDeposits: number;
    pendingOfflineDeposits: number;
  }> => {
    const response = await depositApi.getUserPendingDeposits();
    if (
      response.isSuccess &&
      response.data?.details &&
      Array.isArray(response.data.details) &&
      response.data.details.length > 0
    ) {
      return await depositv1Service.checkPendingDepositTypes(
        response.data.details
      );
    }
    else if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    return {
      pendingOnlineDeposits: 0,
      pendingOfflineDeposits: 0,
    };
  },

  checkPendingDepositTypes: async (
    details: UserPendingDepositsResponseList[]
  ): Promise<{
    pendingOnlineDeposits: number;
    pendingOfflineDeposits: number;
  }> => {
    const pendingOnlineDeposits = details.filter(
      (d) =>
        d.depositmethodtype &&
        d.depositmethodtype !== DepositMethodType.QR &&
        d.depositmethodtype !== DepositMethodType.Offline &&
        (d.status.toLowerCase() === DepositStatus.Pending.toLowerCase() ||
          d.status.toLowerCase() === DepositStatus.InProgress.toLowerCase())
    ).length;

    const pendingOfflineDeposits = details.filter(
      (d) =>
        d.depositmethodtype &&
        (d.depositmethodtype === DepositMethodType.QR ||
          d.depositmethodtype === DepositMethodType.Offline) &&
        (d.status.toLowerCase() === DepositStatus.Pending.toLowerCase() ||
          d.status.toLowerCase() === DepositStatus.InProgress.toLowerCase())
    ).length;

    return { pendingOnlineDeposits, pendingOfflineDeposits };
  },

  getUserDepositSummary:
    async (): Promise<UserDepositSummaryResponse | null> => {
      const response = await depositApi.getUserDepositSummary();
      if (response.isSuccess && response.data) {
        return response.data;
      }
      else if (!response.isSuccess) {
        errorModal.showErrorModal(response.statusCode, response.message);
      }
      return null;
    },

  getDepositTypes: async (
    summary: UserDepositSummaryResponse
  ): Promise<DepositTypeListResponseList[] | null> => {
    const response = await depositApi.getDepositTypes(true);

    if (response.isSuccess && response.data?.details) {
      const validDepositTypes = response.data.details.filter((bank) =>
        depositv1Service.isDepositTypeAllowed(bank, summary)
      );
      return validDepositTypes;
    }
    else if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    return null
  },

  isDepositTypeAllowed: (
    bank: DepositTypeListResponseList,
    summary: UserDepositSummaryResponse
  ): boolean => {
    if (!bank || !summary) return false;

    if (!bank.isDepositAmount && !bank.isDepositTotal && !bank.isAccountAge) {
      return true;
    }

    let isValid = true;

    if (bank.isDepositAmount) {
      isValid =
        isValid && (summary.depositAmount ?? 0) >= (bank.depositAmount ?? 0);
    }

    if (bank.isDepositTotal) {
      isValid = isValid && summary.depositCount >= bank.depositNumber;
    }

    if (bank.isAccountAge) {
      isValid = isValid && summary.daysOld >= bank.accountAge;
    }

    return isValid;
  },

  getUserMobileDetail:
    async (): Promise<UserMobileNumberDetailResponse | null> => {
      const response = await depositApi.getUserMobileDetail();
      if (response.isSuccess && response.data) {
        return response.data;
      }
      else if (!response.isSuccess) {
        errorModal.showErrorModal(response.statusCode, response.message);
      }
      return null;
    },

  getAvailableDepositBonuses:
    async (accessModeSource: string): Promise<AvailableDepositBonusesResponse | null> => {
      const request: AvailableDepositBonusesRequest = {
        accessMode: accessModeSource.toLowerCase() === AccessMode.App.toLowerCase() ? 2 : 1,
      };
      const response = await depositApi.getAvailableDepositBonuses(request);
      if (response.isSuccess && response.data) {
        return response.data;
      }
      else if (!response.isSuccess) {
        errorModal.showErrorModal(response.statusCode, response.message);
      }
      return null;
    },

  getDepositConfiguration:
    async (): Promise<DepositConfigurationResponse | null> => {
      const response = await depositApi.getDepositConfiguration();

      if (response.isSuccess && response.data) {
        return response.data;
      }

      else if (!response.isSuccess) {
        errorModal.showErrorModal(response.statusCode, response.message);
      }
      return null;
    },

  validateBonusEligibility: async (
    bonusId: string
  ): Promise<ValidateBonusEligibilityResponse | null> => {
    const request: ValidateBonusEligibilityRequest = {
      bonusId: bonusId,
    };
    const response = await depositApi.validateBonusEligibility(request);
    if (response.isSuccess && response.data) {
      return response.data;
    }
    else if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    return null;
  },

  handleDepositNextStep: async (
    accessModeSource: string,
    depositFlow: string,
    amount: string,
    selectedBonus: AvailableDepositBonusesList | null,
    selectedDepositType: DepositTypeListResponseList,
    userMobileDetail: UserMobileNumberDetailResponse | null,
    transferTypeList: BankTransferMethod[],
    userDepositSummary?: UserDepositSummaryResponse | null,
    resolvedUpiMethodName?: string | null,
    isBankTransfer?: boolean,
  ): Promise<{ success: boolean; status: string; message?: string, data?: any, gatewayHubRedirectUrl?: string }> => {
    if (depositFlow === DepositFlow.OldFlow) {
      if (
        selectedDepositType.name &&
        (selectedDepositType.name.toLowerCase() === DepositType.ABC ||
          selectedDepositType.name.toLowerCase() === DepositType.ABCUTR ||
          selectedDepositType.name.toLowerCase() === DepositType.ABCUTRTWO ||
          selectedDepositType.name.toLowerCase() === DepositType.ABCUTRTHREE ||
          selectedDepositType.name.toLowerCase() === DepositType.ABCUTRFOUR ||
          selectedDepositType.name.toLowerCase() === DepositType.ABCUTRFIVE ||
          selectedDepositType.name.toLowerCase() === DepositType.ABCNONUTRTWO)
      ) {
        const request: AbcPaymentDepositInitiateReuest = {
          amount: Number(amount),
          bonusId: selectedBonus ? selectedBonus.id : null,
          transactionType:
            transferTypeList.length > 0 ? transferTypeList[0].Type : null,
          bonusDepositBifercationId: selectedBonus
            ? selectedBonus.bonusdepositbifercationid
            : null,
          bankId: selectedDepositType.id,
          paymentMethod: selectedDepositType.name,
          accessMode: accessModeSource.toLowerCase() === AccessMode.App.toLowerCase() ? 2 : 1,
          depositFlow: depositFlow,
        };

        const response = await depositApi.abcDepositInitiate(request);

        if (!response.isSuccess) {
          return {
            success: false,
            status: "error",
            message: response.message.includes("\"error\":true") ? localization.REQUEST_FROM_BLOCKED_USER : response.message,
          };
        }

        if (response.isSuccess && response.data) {
          let appId: string | null | undefined = null;

          switch (selectedDepositType.name?.toLowerCase()) {
            case DepositType.ABC:
              appId = abcAppId;
              break;

            case DepositType.ABCUTR:
              appId = abcUtrAppId;
              break;

            case DepositType.ABCUTRTWO:
              appId = abcUtrTwoAppId;
              break;

            case DepositType.ABCUTRTHREE:
              appId = abcUtrThreeAppId;
              break;

            case DepositType.ABCUTRFOUR:
              appId = abcUtrFourAppId;
              break;

            case DepositType.ABCUTRFIVE:
              appId = abcUtrFiveAppid;
              break;

            case DepositType.ABCNONUTRTWO:
              appId = abcNonUtrTwoAppId;
              break;

            default:
              appId = null;
          }
          if (appId) {
            await loadScript(abcPgUrl);
            await ABCDOMContentLoaded(appId, response.data.abcreferenceid);
            return {
              success: true,
              status: "redirected",
            };
          }
        }
      } else if (
        selectedDepositType.name &&
        selectedDepositType.name.toLowerCase() === DepositType.ABCCrypto
      ) {
        const request: AbcPaymentDepositInitiateReuest = {
          amount: Number(amount),
          bonusId: selectedBonus ? selectedBonus.id : null,
          transactionType:
            transferTypeList.length > 0 ? transferTypeList[0].Type : null,
          bonusDepositBifercationId: selectedBonus
            ? selectedBonus.bonusdepositbifercationid
            : null,
          bankId: selectedDepositType.id,
          mobile: userMobileDetail?.mobile ?? null,
          accessMode:
            accessModeSource.toLowerCase() === AccessMode.App.toLowerCase() ? 2 : 1,
          depositFlow: depositFlow,
        }

        const response = await depositApi.abcCryptoDepositInitiate(request);

        if (!response.isSuccess) {
          return {
            success: false,
            status: "error",
            message: response.message,
          };
        }
        if (response.isSuccess && response.data) {
          await loadScript(abcCryptoUrl);
          await ABCCryptoDOMContentLoaded(abcCryptoAppId, response.data.abcreferenceid);
          return {
            success: true,
            status: "redirected",
          };
        }
      } else if (
        selectedDepositType.name &&
        selectedDepositType.name.toLowerCase() === DepositType.ABCBANKTRANSFER
      ) {
        const request: AbcPaymentDepositInitiateReuest = {
          amount: Number(amount),
          bonusId: selectedBonus ? selectedBonus.id : null,
          transactionType:
            transferTypeList.length > 0 ? transferTypeList[0].Type : null,
          bonusDepositBifercationId: selectedBonus
            ? selectedBonus.bonusdepositbifercationid
            : null,
          bankId: selectedDepositType.id,
          paymentMethod: selectedDepositType.name,
          accessMode: accessModeSource.toLowerCase() === AccessMode.App.toLowerCase() ? 2 : 1,
          depositFlow: depositFlow,
          isBankTransfer: true,
        }

        const response = await depositApi.abcDepositInitiate(request);

        if (!response.isSuccess) {
          return {
            success: false,
            status: "error",
            message: response.message.includes("\"error\":true") ? localization.REQUEST_FROM_BLOCKED_USER : response.message,
          };
        }

        if (response.isSuccess && response.data) {
          return {
            success: true,
            status: "success",
            data: response.data,
          };
        }
      } else if (
        selectedDepositType.depositMethodType &&
        selectedDepositType.depositMethodType.toLowerCase() ===
        DepositMethodType.DT.toLowerCase()
      ) {
        const request: DragonDepositCreateRequest = {
          amount: Number(amount),
          ppaName: selectedDepositType.ppaName,
          baseUrl: window.location.origin,
          bonusId: selectedBonus ? selectedBonus.id : null,
          transactionType:
            transferTypeList.length > 0 ? transferTypeList[0].Type : null,
          bonusDepositBifercationId: selectedBonus
            ? selectedBonus.bonusdepositbifercationid
            : null,
          bankId: selectedDepositType.id,
          accessMode: accessModeSource.toLowerCase() === AccessMode.App.toLowerCase() ? 2 : 1,
          depositFlow: depositFlow,
        }

        const response = await depositApi.dragonDepositSubmit(request);

        if (!response.isSuccess) {
          return {
            success: false,
            status: "error",
            message: response.message,
          };
        }
        if (response.isSuccess && response.data) {

          window.location.href = response.data.pgredirecturl;
          return {
            success: true,
            status: "redirected",
          };
        }
      } else if (
        selectedDepositType.depositMethodType &&
        selectedDepositType.depositMethodType.toLowerCase() ===
        DepositMethodType.GatewayHub.toLowerCase()
      ) {
        const response = await depositApi.getGatewayHubUserId();

        if (!response.isSuccess) {
          return {
            success: false,
            status: "error",
            message: response.message,
          };
        }
        if (response.isSuccess && response.data) {
          if (Number(amount) >= 100) {
            const request: GatewayHubDepositCreateRequest = {
              amount: Number(amount),
              bankId: selectedDepositType.id,
              bonusId: selectedBonus ? selectedBonus.id : null,
              transactionType:
                transferTypeList.length > 0 ? transferTypeList[0].Type : null,
              bonusDepositBifercationId: selectedBonus
                ? selectedBonus.bonusdepositbifercationid
                : null,
              accessMode: accessModeSource.toLowerCase() === AccessMode.App.toLowerCase() ? 2 : 1,
              depositFlow: depositFlow,
              user: response.data,
            }

            const result = await depositApi.gatewayHubDepositSubmit(request);

            if (!result.isSuccess) {
              return {
                success: false,
                status: "error",
                message: result.message,
              };
            }
            if (result.isSuccess && result.data) {
              return {
                success: true,
                status: "success",
                gatewayHubRedirectUrl: result.data.pgredirecturl,
              };
            }
          }
          else {
            return {
              success: false,
              status: "error",
              message: localization.deposit_amount_less_than_hundred,
            };
          }
        }
      }
      else if (
        selectedDepositType.depositMethodType &&
        selectedDepositType.depositMethodType.toLowerCase() ===
        DepositMethodType.PayzEasy.toLowerCase()
      ) {
        const request: PayzEasyDepositInitiateRequest = {
          amount: Number(amount),
          bonusId: selectedBonus ? selectedBonus.id : null,
          transactionType:
            transferTypeList.length > 0 ? transferTypeList[0].Type : null,
          bonusDepositBifercationId: selectedBonus
            ? selectedBonus.bonusdepositbifercationid
            : null,
          bankId: selectedDepositType.id,
          accessMode: accessModeSource.toLowerCase() === AccessMode.App.toLowerCase() ? 2 : 1,
          depositFlow: depositFlow,
          baseUrl: window.location.origin,
        };

        const response = await depositApi.payzEasyDepositSubmit(request);

        if (!response.isSuccess) {
          return {
            success: false,
            status: "error",
            message: response.message.includes("\"status\":\"error\"") ? localization.REQUEST_FROM_BLOCKED_USER : response.message,
          };
        }
        if (response.isSuccess && response.data) {

          window.location.href = response.data.pgredirecturl;
          return {
            success: true,
            status: "redirected",
          };
        }
      }
      else {
        const response = await depositApi.validateUserDepositRequest(
          Number(amount)
        );

        if (!response.isSuccess) {
          return {
            success: false,
            status: "error",
            message: response.message,
          };
        }
        if (response.isSuccess && response.data) {
          if (response.data.isvalid) {
            return {
              success: true,
              status: "success",
            };
          } else {
            return {
              success: false,
              status: "error",
              message: response.data.message,
            };
          }
        }
      }
    }
    else {
      if (
        selectedDepositType.name &&
        (selectedDepositType.name.toLowerCase() === DepositType.ABC ||
          selectedDepositType.name.toLowerCase() === DepositType.ABCUTR ||
          selectedDepositType.name.toLowerCase() === DepositType.ABCUTRTWO ||
          selectedDepositType.name.toLowerCase() === DepositType.ABCUTRTHREE ||
          selectedDepositType.name.toLowerCase() === DepositType.ABCUTRFOUR ||
          selectedDepositType.name.toLowerCase() === DepositType.ABCUTRFIVE ||
          selectedDepositType.name.toLowerCase() === DepositType.ABCNONUTRTWO ||
          selectedDepositType.name.toLowerCase() === DepositType.ABCBANKTRANSFER)
      ) {
        const request: AbcPaymentDepositInitiateReuest = {
          amount: Number(amount),
          bonusId: selectedBonus ? selectedBonus.id : null,
          transactionType:
            transferTypeList.length > 0 ? transferTypeList[0].Type : null,
          bonusDepositBifercationId: selectedBonus
            ? selectedBonus.bonusdepositbifercationid
            : null,
          bankId: selectedDepositType.id,
          paymentMethod: selectedDepositType.name,
          accessMode: accessModeSource.toLowerCase() === AccessMode.App.toLowerCase() ? 2 : 1,
          isBankTransfer: selectedDepositType.name.toLowerCase() === DepositType.ABCBANKTRANSFER ? true : false,
          age: userDepositSummary?.daysOld ?? 0,
          userTransactionAmount: userDepositSummary?.depositAmount ?? 0,
          userTransactionCount: userDepositSummary?.depositCount ?? 0,
          abcBankId: selectedDepositType.allowedABCBankTransfers?.bankId ?? null,
          isMultipleBankFlow: true,
          isAbcBankTransfer: isBankTransfer,
          bankName: selectedDepositType.allowedABCBankTransfers?.bankName ?? null,
          upiMethodName: resolvedUpiMethodName,
          depositFlow: depositFlow,
        };

        const response = await depositApi.abcDepositInitiate(request);

        if (!response.isSuccess) {
          return {
            success: false,
            status: "error",
            message: response.message.includes("\"error\":true") ? localization.REQUEST_FROM_BLOCKED_USER : response.message,
          };
        }

        if (response.isSuccess && response.data) {
          return {
            success: true,
            status: "success",
            data: response.data,
          };
        }
      }
      else if (selectedDepositType.name &&
        selectedDepositType.name.toLowerCase() === DepositType.ABCCrypto) {
        const request: AbcPaymentDepositInitiateReuest = {
          amount: Number(amount),
          bonusId: selectedBonus ? selectedBonus.id : null,
          transactionType:
            transferTypeList.length > 0 ? transferTypeList[0].Type : null,
          bonusDepositBifercationId: selectedBonus
            ? selectedBonus.bonusdepositbifercationid
            : null,
          bankId: selectedDepositType.id,
          mobile: userMobileDetail?.mobile ?? null,
          accessMode: accessModeSource.toLowerCase() === AccessMode.App.toLowerCase() ? 2 : 1,
          isAbcBankTransfer: isBankTransfer,
          upiMethodName: resolvedUpiMethodName,
          depositFlow: depositFlow,
        }

        const response = await depositApi.abcCryptoDepositInitiate(request);

        if (!response.isSuccess) {
          return {
            success: false,
            status: "error",
            message: response.message,
          };
        }
        if (response.isSuccess && response.data) {
          await loadScript(abcCryptoUrl);
          await ABCCryptoDOMContentLoaded(abcCryptoAppId, response.data.abcreferenceid);
          return {
            success: true,
            status: "redirected",
          };
        }
      }
      else if (selectedDepositType.depositMethodType &&
        selectedDepositType.depositMethodType.toLowerCase() ===
        DepositMethodType.DT.toLowerCase()) {
        const request: DragonDepositCreateRequest = {
          amount: Number(amount),
          ppaName: selectedDepositType.ppaName,
          baseUrl: window.location.origin,
          bonusId: selectedBonus ? selectedBonus.id : null,
          transactionType:
            transferTypeList.length > 0 ? transferTypeList[0].Type : null,
          bonusDepositBifercationId: selectedBonus
            ? selectedBonus.bonusdepositbifercationid
            : null,
          bankId: selectedDepositType.id,
          accessMode: accessModeSource.toLowerCase() === AccessMode.App.toLowerCase() ? 2 : 1,
          isBankTransfer: isBankTransfer,
          bankName: null,
          upiMethodName: resolvedUpiMethodName,
          depositFlow: depositFlow,
        }

        const response = await depositApi.dragonDepositSubmit(request);

        if (!response.isSuccess) {
          return {
            success: false,
            status: "error",
            message: response.message,
          };
        }

        if (response.isSuccess && response.data) {

          window.location.href = response.data.pgredirecturl;
          return {
            success: true,
            status: "redirected",
          };
        }
      }
      else if (selectedDepositType.depositMethodType &&
        selectedDepositType.depositMethodType.toLowerCase() ===
        DepositMethodType.GatewayHub.toLowerCase()) {
        const response = await depositApi.getGatewayHubUserId();


        if (!response.isSuccess) {
          return {
            success: false,
            status: "error",
            message: response.message,
          };
        }

        if (response.isSuccess && response.data) {

          if (Number(amount) < 100) {
            return {
              success: false,
              status: "error",
              message: localization.deposit_amount_less_than_hundred,
            };
          }
          if (Number(amount) >= 100) {
            const request: GatewayHubDepositCreateRequest = {
              amount: Number(amount),
              bankId: selectedDepositType.id,
              bonusId: selectedBonus ? selectedBonus.id : null,
              transactionType:
                transferTypeList.length > 0 ? transferTypeList[0].Type : null,
              bonusDepositBifercationId: selectedBonus
                ? selectedBonus.bonusdepositbifercationid
                : null,
              accessMode: accessModeSource.toLowerCase() === AccessMode.App.toLowerCase() ? 2 : 1,
              depositFlow: depositFlow,
              user: response.data,
              upiMethodName: resolvedUpiMethodName,
            }

            const result = await depositApi.gatewayHubDepositSubmit(request);

            if (!result.isSuccess) {
              return {
                success: false,
                status: "error",
                message: result.message,
              };
            }
            if (result.isSuccess && result.data) {
              return {
                success: true,
                status: "success",
                gatewayHubRedirectUrl: result.data.pgredirecturl,
              };
            }
          }
        }
      }
      else if (
        selectedDepositType.depositMethodType &&
        selectedDepositType.depositMethodType.toLowerCase() ===
        DepositMethodType.PayzEasy.toLowerCase()
      ) {
        const request: PayzEasyDepositInitiateRequest = {
          amount: Number(amount),
          bonusId: selectedBonus ? selectedBonus.id : null,
          transactionType:
            transferTypeList.length > 0 ? transferTypeList[0].Type : null,
          bonusDepositBifercationId: selectedBonus
            ? selectedBonus.bonusdepositbifercationid
            : null,
          bankId: selectedDepositType.id,
          accessMode: accessModeSource.toLowerCase() === AccessMode.App.toLowerCase() ? 2 : 1,
          depositFlow: depositFlow,
          baseUrl: window.location.origin,
          isBankTransfer: isBankTransfer,
          bankName: null,
          upiMethodName: resolvedUpiMethodName,
        };

        const response = await depositApi.payzEasyDepositSubmit(request);

        if (!response.isSuccess) {
          return {
            success: false,
            status: "error",
            message: response.message.includes("\"status\":\"error\"") ? localization.REQUEST_FROM_BLOCKED_USER : response.message,
          };
        }
        if (response.isSuccess && response.data) {

          window.location.href = response.data.pgredirecturl;
          return {
            success: true,
            status: "redirected",
          };
        }
      }
      else {
        const response = await depositApi.validateUserDepositRequest(
          Number(amount)
        );

        if (!response.isSuccess) {
          return {
            success: false,
            status: "error",
            message: response.message,
          };
        }

        if (response.isSuccess && response.data) {
          if (response.data.isvalid) {
            return {
              success: true,
              status: "success",
            };
          } else {
            return {
              success: false,
              status: "error",
              message: response.data.message,
            };
          }
        }
      }
    }
    return {
      success: false,
      status: "error",
      message: "Unable to process deposit request",
    };
  },

  parseBankTransferMethods: (
    proofJson?: string | null
  ): BankTransferMethod[] => {
    if (!proofJson) return [];

    const parsed = JSON.parse(proofJson);
    return Array.isArray(parsed) ? (parsed as BankTransferMethod[]) : [];
  },

  handleDepositSubmit: async (
    depositFlow: string,
    amount: string,
    referenceNumber: string,
    selectedTransferType: string,
    selectedDepositType: DepositTypeListResponseList,
    selectedBonus: AvailableDepositBonusesList | null,
    accessModeSource: string
  ): Promise<DepositCreateResponse | null> => {
    const request: DepositCreateRequest = {
      amount: Number(amount),
      bankId: selectedDepositType.id,
      bonusId: selectedBonus ? selectedBonus.id : null,
      bonusDepositBifercationId: selectedBonus
        ? selectedBonus.bonusdepositbifercationid
        : null,
      referenceNumber: referenceNumber,
      transactionType: selectedTransferType,
      accessMode: accessModeSource.toLowerCase() === AccessMode.App.toLowerCase() ? 2 : 1,
      depositFlow: depositFlow,
    };

    const response = await depositApi.submitDeposit(request);

    if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    else if (response.isSuccess && response.data) {
      return response.data;
    }
    return null;
  },

  getUserPendingDepositsWithDetails: async (): Promise<
    UserPendingDepositsResponseList[] | null
  > => {
    const response = await depositApi.getUserPendingDeposits();
    if (response.isSuccess) {
      return response.data?.details ?? null;
    }
    else {
      errorModal.showErrorModal(response.statusCode, response.message);
      return null;
    }
  },

  handleAbcDepositSubmit: async (
    referenceNumber: string,
    selectedDepositType: DepositTypeListResponseList,
    depositCreateResponse: DepositCreateResponse | null,
    isBankTransfer: boolean
  ): Promise<any | null> => {
    const request: AbcDepositCreateRequest = {
      abcBankId: selectedDepositType.allowedABCBankTransfers?.bankId ?? "",
      abcReferenceId: depositCreateResponse?.abcreferenceid ?? "",
      referenceNumber: referenceNumber,
      paymentMethod: selectedDepositType.name,
      isBankTransferApiSubmit: isBankTransfer,
    };

    const response = await depositApi.abcDepositSubmit(request);
    if (response.isSuccess && response.data) {
      return response.data;
    }
    else if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    return null;
  },

  getTopRecommendedTransferMethods: async (): Promise<TopRecommendedTransferMethodsResponse | null> => {
    const response = await depositApi.getTopRecommendedTransferMethods();
    if (response.isSuccess && response.data) {
      return response.data;
    }
    else if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    return null;
  },

  getUserRecentUsedDepositTypes: async (): Promise<UserRecentUsedDepositTypesResponse | null> => {
    const response = await depositApi.getUserRecentUsedDepositTypes();
    if (response.isSuccess && response.data) {
      return response.data;
    }
    else if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    return null;
  },

  getDepositTypeWithAbcBankDetails: async (
    request: DepositTypeListRequest,
    summary: UserDepositSummaryResponse,
    topRecommendedTransferMethods: TopRecommendedTransferMethodsResponse | null
  ): Promise<DepositTypeListResponseList[] | null> => {
    const response = await depositApi.getDepositTypeWithAbcBankDetails(request);
    if (response.isSuccess && response.data) {
      const validDepositTypes = response.data.details.filter((bank) =>
        depositv1Service.isDepositTypeAllowed(bank, summary)
      );

      const hasPositiveSequence = validDepositTypes.some((x) => x.sequence > 0);
      if (hasPositiveSequence) {
        const recommendedItem = validDepositTypes.filter((x) => x.sequence > 0).sort((a, b) => a.sequence - b.sequence)[0];
        if (recommendedItem) {
          recommendedItem.isRecommenededTransferMethod = true;
        }
      }
      else {
        validDepositTypes.forEach(item => { item.isRecommenededTransferMethod = false; });
        const totalRows = topRecommendedTransferMethods?.details?.length ?? 0;

        topRecommendedTransferMethods?.details
          ?.filter(r => r.rowNumber <= 10)
          ?.sort((a, b) => a.rowNumber - b.rowNumber)
          ?.some((topBankTransfer) => {
            const allowZeroScoreCase =
              totalRows === 1 &&
              topBankTransfer.finalScore === 0 &&
              topBankTransfer.numberOfApprovedTransactions > 0;

            const match = validDepositTypes.find((x) =>
              x.id === topBankTransfer.depositTypeId &&
              (topBankTransfer.finalScore > 0 || allowZeroScoreCase) &&
              (
                !topBankTransfer.bankName
                  ? x.name === topBankTransfer.name
                  : x.allowedABCBankTransfers &&
                  x.allowedABCBankTransfers.bankName === topBankTransfer.bankName
              )
            );

            if (match) {
              match.isRecommenededTransferMethod = true;
              return true;
            }

            return false;
          });
      }
      return validDepositTypes;
    }

    else if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    return null;
  },

  getDepositPaymentMethod: async (
    depositTypeListResponse: DepositTypeListResponseList[],
    userRecentUsedDepositType: UserRecentUsedDepositTypesResponse | null,
    topRecommendedTransferMethods: TopRecommendedTransferMethodsResponse | null,
  ): Promise<DepositPaymentMethodListResponseList[] | null> => {
    const response = await depositApi.getDepositPaymentMethod();
    if (!response.isSuccess || !response.data) {
      errorModal.showErrorModal(response.statusCode, response.message);
      return null;
    }

    let methodsWithData: DepositPaymentMethodListResponseList[] = [];

    for (const method of response.data.details) {

      const depositTypeListData =
        await depositv1Service.getDepositTypesForMethod(
          method,
          depositTypeListResponse,
          userRecentUsedDepositType
        );

      if (depositTypeListData && depositTypeListData.length > 0) {

        depositTypeListData.forEach(item => {
          if (!item.allowedPaymentMethods?.trim()) {
            item.allowedPaymentMethods = method.name;
          } else {
            const methods = item.allowedPaymentMethods
              .split(",")
              .map(m => m.trim())
              .filter(Boolean);

            if (!methods.some(m => m.toLowerCase() === method.name.toLowerCase())) {
              methods.push(method.name);
            }

            item.allowedPaymentMethods = methods.join(",");
          }
        });

        methodsWithData.push(method);
      }
    }

    if (methodsWithData.length > 0) {
      const recommendedMethodsDict: Record<string, number> = {};
      depositTypeListResponse
        .filter(
          x =>
            x.sequence > 0 &&
            x.allowedPaymentMethods &&
            x.allowedPaymentMethods.trim() !== ""
        )
        .sort((a, b) => a.sequence - b.sequence)
        .flatMap(x =>
          x.allowedPaymentMethods
            .split(",")
            .map(s => s.trim())
            .filter(Boolean)
        )
        .filter(
          (name, index, self) =>
            self.findIndex(
              n => n.toLowerCase() === name.toLowerCase()
            ) === index
        )
        .forEach((name, index) => {
          recommendedMethodsDict[name.toLowerCase()] = index;
        });

      const details = topRecommendedTransferMethods?.details ?? [];
      const allowSingleZeroScore =
        details?.length === 1 &&
        details[0].finalScore === 0 &&
        details[0].numberOfApprovedTransactions > 0;

      const filteredDetails = details.filter(
        r => r.finalScore > 0 || allowSingleZeroScore
      );

      const rankByMethod: Record<string, number> = {};

      const groupedByRank = filteredDetails
        .filter(r =>
          depositTypeListResponse.some(
            b =>
              b.id === r.depositTypeId &&
              b.name?.toLowerCase() === r.name?.toLowerCase()
          )
        )
        .reduce<Record<number, typeof filteredDetails>>((acc, item) => {
          acc[item.upiMethodRank] ??= [];
          acc[item.upiMethodRank].push(item);
          return acc;
        }, {});

      Object.keys(groupedByRank)
        .map(Number)
        .sort((a, b) => a - b)
        .forEach(rank => {
          const group = groupedByRank[rank];

          const mainKey =
            (group[0]?.upiMethodName ?? "")
              .trim()
              .toLowerCase();

          if (mainKey && !(mainKey in rankByMethod)) {
            rankByMethod[mainKey] = rank;
          }

          group
            .sort((a, b) => b.finalScore - a.finalScore)
            .forEach(rItem => {
              methodsWithData
                .filter(
                  pm =>
                    pm.ismanualcreation &&
                    pm.deposittypes
                      ?.split(",")
                      .map(x => x.trim())
                      .includes(rItem.depositTypeId)
                )
                .forEach(pm => {
                  const key = pm.name?.trim().toLowerCase();
                  if (key && !(key in rankByMethod)) {
                    rankByMethod[key] = rank;
                  }
                });
            });
        });

      methodsWithData = [...methodsWithData].sort((a, b) => {
        const favA = a.name?.trim().toLowerCase() === "favorite";
        const favB = b.name?.trim().toLowerCase() === "favorite";
        if (favA !== favB) return favA ? -1 : 1;

        const seqA =
          recommendedMethodsDict[a.name?.toLowerCase() ?? ""] ??
          Number.MAX_SAFE_INTEGER;
        const seqB =
          recommendedMethodsDict[b.name?.toLowerCase() ?? ""] ??
          Number.MAX_SAFE_INTEGER;

        if (seqA !== seqB) return seqA - seqB;

        const rankA =
          rankByMethod[a.name?.toLowerCase() ?? ""] ??
          Number.MAX_SAFE_INTEGER;
        const rankB =
          rankByMethod[b.name?.toLowerCase() ?? ""] ??
          Number.MAX_SAFE_INTEGER;

        return rankA - rankB;
      });
    }
    return methodsWithData;
  },

  getDepositTypesForMethod: async (
    method: DepositPaymentMethodListResponseList,
    depositTypeListResponse: DepositTypeListResponseList[],
    userRecentUsedDepositTypes: UserRecentUsedDepositTypesResponse | null):
    Promise<DepositTypeListResponseList[] | null> => {
    let manualDepositTypeIds: string[] = [];
    if (method.deposittypes) {
      manualDepositTypeIds = method.deposittypes
        .split(",")
        .map(id => id.trim())
        .filter(Boolean);
    }
    const methodName = method.name?.toLowerCase();

    switch (methodName) {
      case DepositPaymentMethod.BankTransfer:
        return depositTypeListResponse.filter(x =>
          x.depositMethodType &&
          (
            (
              x.depositMethodType.toLowerCase() === "abc" &&
              x.allowedABCBankTransfers?.isBankTransferAllowed === true
            )
            || (
              (
                x.depositMethodType.toLowerCase() === DepositMethodType.QR.toLowerCase() ||
                x.depositMethodType.toLowerCase() === DepositMethodType.Offline.toLowerCase()
              ) &&
              (!x.depositPaymentMethod ||
                x.depositPaymentMethod.toLowerCase() === methodName)
            )
            || (
              method.ismanualcreation &&
              manualDepositTypeIds.includes(x.id)
            )
          )
        );

      case DepositPaymentMethod.Gpay:
      case DepositPaymentMethod.Phonepe:
      case DepositPaymentMethod.Bhim:
      case DepositPaymentMethod.Paytm:
        return depositTypeListResponse.filter(x =>
          x.depositMethodType &&
          (
            (
              (
                x.depositMethodType.toLowerCase() === DepositMethodType.QR.toLowerCase() ||
                x.depositMethodType.toLowerCase() === DepositMethodType.Offline.toLowerCase()
              ) &&
              x.depositPaymentMethod?.toLowerCase() === methodName
            )
            || (
              x.depositMethodType.toLowerCase() === "abc" &&
              x.allowedABCBankTransfers?.allowedUpi?.some(
                upi => upi.toLowerCase() === methodName
              )
            )
            || (
              method.ismanualcreation &&
              manualDepositTypeIds.includes(x.id)
            )
          )
        );

      case DepositPaymentMethod.Crypto.toLowerCase():
        return depositTypeListResponse.filter(
          x => x.name?.toLowerCase() === DepositType.ABCCrypto.toLowerCase()
        );

      case DepositPaymentMethod.Favorite:
        if (!userRecentUsedDepositTypes?.details?.length) return [];

        const recentDetails = userRecentUsedDepositTypes.details;

        return depositTypeListResponse
          .filter(bank =>
            recentDetails.some((user: any) =>
              (
                user.bankName &&
                bank.allowedABCBankTransfers?.bankName &&
                bank.allowedABCBankTransfers.bankName.toLowerCase() === user.bankName.toLowerCase() &&
                bank.id === user.depositTypeId
              )
              ||
              (
                !user.bankName &&
                user.paymentType &&
                bank.name?.toLowerCase() === user.paymentType.toLowerCase() &&
                bank.id === user.depositTypeId
              )
            )
          )
          .sort(
            (a, b) =>
              recentDetails.findIndex((u: any) => u.depositTypeId === a.id) -
              recentDetails.findIndex((u: any) => u.depositTypeId === b.id)
          );

      default:
        return depositTypeListResponse.filter(x =>
          (
            x.depositMethodType?.toLowerCase() === methodName
          ) ||
          (
            x.name?.toLowerCase() === methodName
          ) ||
          (
            method.ismanualcreation &&
            manualDepositTypeIds.includes(x.id)
          )
        );
    }
  },

  resolveUpiMethodName: (
    selectedDepositPaymentMethod: DepositPaymentMethodListResponseList | null,
    selectedDepositType: DepositTypeListResponseList | null,
    topRecommendedTransferMethods: TopRecommendedTransferMethodsResponse | null,
  ): string | null => {
    const depositMethodType = selectedDepositType?.depositMethodType?.toLowerCase() ?? null;
    const upiMethodName = selectedDepositPaymentMethod?.name ?? null;

    switch (depositMethodType) {
      case DepositMethodType.QR.toLowerCase():
      case DepositMethodType.Offline.toLowerCase():
        return (
          selectedDepositType?.depositMethodType ??
          upiMethodName
        );

      case DepositMethodType.DT.toLowerCase():
        return DepositMethodType.DT;

      case DepositMethodType.GatewayHub.toLowerCase():
        return DepositMethodType.GatewayHub;

      case DepositMethodType.ABC.toLowerCase(): {
        const recommendedUpi =
          topRecommendedTransferMethods?.details?.find(
            (x) =>
              x.depositTypeId === selectedDepositType?.id &&
              (x.bankName ?? "") ===
              (selectedDepositType?.allowedABCBankTransfers?.bankName ?? "")
          )?.upiMethodName;

        if (recommendedUpi) return recommendedUpi;

        if (selectedDepositPaymentMethod?.name?.toLowerCase() !== DepositPaymentMethod.BankTransfer) {
          const allowedUpi =
            selectedDepositType?.allowedABCBankTransfers?.allowedUpi;

          if (allowedUpi && allowedUpi.length > 0) {
            return allowedUpi[0];
          }
          return upiMethodName;
        }

        if (
          selectedDepositType?.allowedABCBankTransfers
            ?.isBankTransferAllowed
        ) {
          return DepositPaymentMethod.BankTransferAlt;
        }

        return upiMethodName;
      }
      default:
        return upiMethodName;
    }
  },
};

export default depositv1Service;